import { platformVersionedIdentifierSchema } from '@crest/platform-contracts/modules';
import { z } from 'zod';
export const workflowsRulesCapability = 'workflows.rules.v1';
export const workflowsRulePreviewCapability = 'workflows.rule-preview.v1';
export const workflowsRuleIdSchema = z.string().uuid();
export const workflowsRuleVersionIdSchema = z.string().uuid();
export const workflowsCommandIdSchema = z.string().uuid();
export const workflowsRuleStatusSchema = z.enum(['draft', 'active', 'disabled', 'archived']);
export const workflowsConditionModeSchema = z.enum(['all', 'any']);
export const workflowsConditionOperatorSchema = z.enum([
    'equals',
    'not_equals',
    'exists',
    'contains',
    'one_of',
    'greater_than_or_equal',
    'less_than_or_equal',
]);
export const workflowsJsonPrimitiveSchema = z.union([
    z.string().max(2_000),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);
export const workflowsConditionClauseSchema = z.strictObject({
    path: z.string().trim().min(1).max(240).regex(/^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*){0,4}$/, 'Condition paths use at most five dot-separated object keys.'),
    operator: workflowsConditionOperatorSchema,
    value: workflowsJsonPrimitiveSchema,
});
export const workflowsActionReferenceSchema = z.strictObject({
    actionId: platformVersionedIdentifierSchema,
    configuration: z.record(z.string().min(1).max(120), z.unknown()).default({}),
});
export const workflowsRuleDefinitionSchema = z.strictObject({
    triggerEvent: platformVersionedIdentifierSchema,
    conditionMode: workflowsConditionModeSchema.default('all'),
    conditions: z.array(workflowsConditionClauseSchema).max(25).default([]),
    actionReferences: z.array(workflowsActionReferenceSchema).max(10).default([]),
});
export const workflowsRuleVersionSchema = workflowsRuleDefinitionSchema.extend({
    id: workflowsRuleVersionIdSchema,
    tenantId: z.string().uuid(),
    ruleId: workflowsRuleIdSchema,
    versionNumber: z.number().int().positive(),
    sourceVersionId: workflowsRuleVersionIdSchema.nullable(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().min(1).max(500),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const workflowsRuleSchema = z.strictObject({
    id: workflowsRuleIdSchema,
    tenantId: z.string().uuid(),
    name: z.string().min(1).max(160),
    description: z.string().max(1_000).nullable(),
    status: workflowsRuleStatusSchema,
    enabled: z.boolean(),
    activeVersionId: workflowsRuleVersionIdSchema.nullable(),
    latestVersionId: workflowsRuleVersionIdSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    activeVersion: workflowsRuleVersionSchema.nullable(),
    latestVersion: workflowsRuleVersionSchema,
});
export const workflowsRuleListQuerySchema = z.strictObject({
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
});
const workflowsCommandMetadataSchema = z.strictObject({
    commandId: workflowsCommandIdSchema,
    idempotent: z.boolean(),
});
export const workflowsCreateRuleSchema = z.strictObject({
    commandId: workflowsCommandIdSchema,
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1_000).nullable().default(null),
    definition: workflowsRuleDefinitionSchema,
    reason: z.string().trim().min(5).max(500),
});
export const workflowsAppendRuleVersionSchema = z.strictObject({
    commandId: workflowsCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    definition: workflowsRuleDefinitionSchema,
    reason: z.string().trim().min(5).max(500),
});
export const workflowsPublishRuleVersionSchema = z.strictObject({
    commandId: workflowsCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    versionId: workflowsRuleVersionIdSchema,
    reason: z.string().trim().min(10).max(500),
});
export const workflowsRuleStateCommandSchema = z.strictObject({
    commandId: workflowsCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const workflowsRollbackRuleSchema = workflowsRuleStateCommandSchema.extend({
    sourceVersionId: workflowsRuleVersionIdSchema,
});
export const workflowsPreviewEventSchema = z.strictObject({
    eventName: platformVersionedIdentifierSchema,
    occurredAt: z.string().datetime({ offset: true }),
    payload: z.record(z.string().min(1).max(120), z.unknown()),
});
export const workflowsRulePreviewRequestSchema = z.strictObject({
    definition: workflowsRuleDefinitionSchema,
    event: workflowsPreviewEventSchema,
});
export const workflowsClauseEvaluationSchema = z.strictObject({
    index: z.number().int().nonnegative(),
    path: z.string(),
    operator: workflowsConditionOperatorSchema,
    actual: z.unknown(),
    matched: z.boolean(),
});
export const workflowsRulePreviewResponseSchema = z.strictObject({
    data: z.strictObject({
        triggerMatched: z.boolean(),
        conditionsMatched: z.boolean(),
        matched: z.boolean(),
        clauseEvaluations: z.array(workflowsClauseEvaluationSchema),
        actionReferences: z.array(workflowsActionReferenceSchema),
    }),
});
export const workflowsRuleResponseSchema = z.strictObject({
    data: workflowsRuleSchema,
    command: workflowsCommandMetadataSchema.optional(),
});
export const workflowsRulesResponseSchema = z.strictObject({
    data: z.array(workflowsRuleSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const workflowsRuleVersionsResponseSchema = z.strictObject({
    data: z.array(workflowsRuleVersionSchema),
});
//# sourceMappingURL=workflows-rule.contract.js.map